import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppSwitch from '../../../../../../../components/common/AppSwitch';
import AppSingleSlider from '../../../../../../../components/common/AppSingleSlider';
import AppRangeSlider from '../../../../../../../components/common/AppRangeSlider';
import {FormDataType, RangeFormType, RangeType} from '../../../types';

type Props = {
  index: number;
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  errors: any;
};

type RangeTypeOption = {value: RangeType; label: string};
type RangeFormOption = {value: RangeFormType; label: string};

const TEXTUAL_FROM_IDS = ['low', 'short', 'cold'] as const;
const TEXTUAL_TO_IDS = ['high', 'long', 'hot'] as const;
type TextualFromId = (typeof TEXTUAL_FROM_IDS)[number];
type TextualToId = (typeof TEXTUAL_TO_IDS)[number];

type TextualFromOption = {value: TextualFromId; label: string};
type TextualToOption = {value: TextualToId; label: string};

function isTextualFromId(s: unknown): s is TextualFromId {
  return (
    typeof s === 'string' && (TEXTUAL_FROM_IDS as readonly string[]).includes(s)
  );
}

function isTextualToId(s: unknown): s is TextualToId {
  return (
    typeof s === 'string' && (TEXTUAL_TO_IDS as readonly string[]).includes(s)
  );
}

function resolveTextualFromId(minLabel: unknown): TextualFromId {
  if (isTextualFromId(minLabel)) return minLabel;
  return 'low';
}

function resolveTextualToId(maxLabel: unknown): TextualToId {
  if (isTextualToId(maxLabel)) return maxLabel;
  return 'high';
}

const TemperatureDegreeIcon = (
  <span className="pointer-events-none text-[14px] text-textColor" aria-hidden>
    °C
  </span>
);

type ResetRangeConfigOptions = {
  index: number;
  setValue: UseFormSetValue<FormDataType>;
  currentFormType?: RangeFormType;
};

function resetRangeConfigForTextual({
  index,
  setValue,
  currentFormType,
}: ResetRangeConfigOptions) {
  setValue(`questions.${index}.rangeConfig.minLabel` as any, 'low', {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.maxLabel` as any, 'high', {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.min` as any, undefined, {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.max` as any, undefined, {
    shouldDirty: true,
  });
  if (currentFormType === 'range') {
    setValue(`questions.${index}.rangeConfig.form` as any, 'between', {
      shouldDirty: true,
    });
  }
}

function resetRangeConfigForNumeric({
  index,
  setValue,
}: ResetRangeConfigOptions) {
  setValue(`questions.${index}.rangeConfig.min` as any, 0, {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.max` as any, 100, {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.minLabel` as any, '', {
    shouldDirty: true,
  });
  setValue(`questions.${index}.rangeConfig.maxLabel` as any, '', {
    shouldDirty: true,
  });
}

function RangeField({index, control, watch, setValue, errors}: Props) {
  const {t} = useTranslation();

  const rangeTypeOptions: RangeTypeOption[] = useMemo(
    () => [
      {value: 'numeric', label: t('forms.dialog.range.type.numeric')},
      {value: 'textual', label: t('forms.dialog.range.type.textual')},
      {value: 'temperature', label: t('forms.dialog.range.type.temperature')},
    ],
    [t],
  );

  const textualFromOptions = useMemo<TextualFromOption[]>(
    () =>
      TEXTUAL_FROM_IDS.map(id => ({
        value: id,
        label: t(`forms.dialog.range.textual.from.${id}`),
      })),
    [t],
  );

  const textualToOptions = useMemo<TextualToOption[]>(
    () =>
      TEXTUAL_TO_IDS.map(id => ({
        value: id,
        label: t(`forms.dialog.range.textual.to.${id}`),
      })),
    [t],
  );

  const rangeType = watch(`questions.${index}.rangeConfig.rangeType` as any) as
    | RangeType
    | undefined;
  const formType = watch(`questions.${index}.rangeConfig.form` as any) as
    | RangeFormType
    | undefined;
  const min = watch(`questions.${index}.rangeConfig.min` as any) as
    | number
    | undefined;
  const max = watch(`questions.${index}.rangeConfig.max` as any) as
    | number
    | undefined;
  const minLabel = watch(`questions.${index}.rangeConfig.minLabel` as any) as
    | string
    | undefined;
  const maxLabel = watch(`questions.${index}.rangeConfig.maxLabel` as any) as
    | string
    | undefined;

  const formOptions: RangeFormOption[] = useMemo(() => {
    const all: RangeFormOption[] = [
      {value: 'between', label: t('forms.dialog.range.form.between')},
      {value: 'range', label: t('forms.dialog.range.form.range')},
      {value: 'from-to', label: t('forms.dialog.range.form.fromTo')},
    ];
    if (rangeType === 'textual') {
      return all.filter(o => o.value !== 'range');
    }
    return all;
  }, [t, rangeType]);

  const showBoundEditors =
    formType === 'between' || formType === 'from-to' || formType === 'range';

  const isNumericLike = rangeType === 'numeric' || rangeType === 'temperature';
  const isTemperature = rangeType === 'temperature';
  const isTextual = rangeType === 'textual';

  const isFormRange = formType === 'range';
  const showNumericRangeFormSlider = isFormRange && isNumericLike;

  const numericSafeMin = Number.isFinite(min) ? Number(min) : 0;
  const numericSafeMax =
    Number.isFinite(max) && Number(max) > numericSafeMin
      ? Number(max)
      : numericSafeMin + 1;

  const textualFromId = resolveTextualFromId(minLabel);
  const textualToId = resolveTextualToId(maxLabel);

  const fromEndLabelText = isTextual
    ? t(`forms.dialog.range.textual.from.${textualFromId}`)
    : '';
  const toEndLabelText = isTextual
    ? t(`forms.dialog.range.textual.to.${textualToId}`)
    : '';

  const [previewValue, setPreviewValue] = useState(50);
  const [rangeSliderDraft, setRangeSliderDraft] = useState<[number, number]>([
    numericSafeMin,
    numericSafeMax,
  ]);

  // Keep the draft in sync when the form values change externally
  // (e.g. switching range type, editing the text inputs directly).
  useEffect(() => {
    setRangeSliderDraft([numericSafeMin, numericSafeMax]);
  }, [numericSafeMin, numericSafeMax]);

  useEffect(() => {
    if (!isTextual) return;
    const wantFrom = resolveTextualFromId(minLabel);
    const wantTo = resolveTextualToId(maxLabel);
    const labelMismatch = minLabel !== wantFrom || maxLabel !== wantTo;
    const numsPresent = min !== undefined || max !== undefined;
    const formInvalid = formType === 'range';
    if (!labelMismatch && !numsPresent && !formInvalid) return;
    setValue(`questions.${index}.rangeConfig.minLabel` as any, wantFrom, {
      shouldDirty: !!numsPresent,
    });
    setValue(`questions.${index}.rangeConfig.maxLabel` as any, wantTo, {
      shouldDirty: !!numsPresent,
    });
    if (numsPresent) {
      setValue(`questions.${index}.rangeConfig.min` as any, undefined, {
        shouldDirty: true,
      });
      setValue(`questions.${index}.rangeConfig.max` as any, undefined, {
        shouldDirty: true,
      });
    }
    if (formInvalid) {
      setValue(`questions.${index}.rangeConfig.form`, 'between', {
        shouldDirty: true,
      });
    }
  }, [isTextual, formType, index, min, max, minLabel, maxLabel, setValue]);

  useEffect(() => {
    if (isFormRange && isNumericLike) return;
    if (isTextual) {
      setPreviewValue(prev =>
        Number.isFinite(prev) && prev >= 0 && prev <= 100 ? prev : 50,
      );
      return;
    }
    setPreviewValue(prev => {
      if (prev < numericSafeMin || prev > numericSafeMax) {
        return Math.round((numericSafeMin + numericSafeMax) / 2);
      }
      return prev;
    });
  }, [isFormRange, isNumericLike, isTextual, numericSafeMin, numericSafeMax]);

  const selectedRangeType =
    rangeTypeOptions.find(o => o.value === rangeType) ?? null;
  const selectedFormType = formOptions.find(o => o.value === formType) ?? null;

  const selectedTextualFrom =
    textualFromOptions.find(o => o.value === textualFromId) ??
    textualFromOptions[0];
  const selectedTextualTo =
    textualToOptions.find(o => o.value === textualToId) ?? textualToOptions[0];

  const numericSliderValue = Math.min(
    Math.max(previewValue, numericSafeMin),
    numericSafeMax,
  );
  const textualSliderValue = Math.min(100, Math.max(0, previewValue));

  return (
    <div className="flex flex-col gap-12">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[140px]">
          <AppMUIAutocomplete<RangeTypeOption, false, true, false>
            size="small"
            fullWidth
            options={rangeTypeOptions}
            value={selectedRangeType ?? rangeTypeOptions[0]}
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_e, val) => {
              if (!val) return;
              setValue(
                `questions.${index}.rangeConfig.rangeType` as any,
                val.value,
                {shouldDirty: true},
              );
              if (val.value === 'textual') {
                resetRangeConfigForTextual({
                  index,
                  setValue,
                  currentFormType: formType,
                });
              } else {
                resetRangeConfigForNumeric({index, setValue});
              }
            }}
            disableClearable
          />
        </div>

        <div className="flex-1 min-w-[140px]">
          <AppMUIAutocomplete<RangeFormOption, false, true, false>
            size="small"
            fullWidth
            options={formOptions}
            value={selectedFormType ?? formOptions[0]}
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            onChange={(_e, val) => {
              if (!val) return;
              setValue(
                `questions.${index}.rangeConfig.form` as any,
                val.value,
                {shouldDirty: true},
              );
            }}
            disableClearable
          />
        </div>

        {showBoundEditors && isNumericLike && (
          <>
            <div className="flex min-w-[160px] flex-1 items-center gap-2">
              <span className="text-textColor shrink-0">
                {t('forms.dialog.range.from')}
              </span>
              <div className="min-w-0 flex-1">
                <Controller
                  name={`questions.${index}.rangeConfig.min` as any}
                  control={control}
                  render={({field}) => (
                    <AppTextInput
                      type="number"
                      value={field.value ?? ''}
                      onChange={e => {
                        const v = (e.target as HTMLInputElement).value;
                        field.onChange(v === '' ? undefined : Number(v));
                      }}
                      className={'border-none'}
                      py={'py-2'}
                      endIcon={
                        isTemperature ? TemperatureDegreeIcon : undefined
                      }
                      error={errors?.rangeConfig?.min?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex min-w-[160px] flex-1 items-center gap-2">
              <span className="text-textColor shrink-0">
                {t('forms.dialog.range.to')}
              </span>
              <div className="min-w-0 flex-1">
                <Controller
                  name={`questions.${index}.rangeConfig.max` as any}
                  control={control}
                  render={({field}) => (
                    <AppTextInput
                      type="number"
                      value={field.value ?? ''}
                      onChange={e => {
                        const v = (e.target as HTMLInputElement).value;
                        field.onChange(v === '' ? undefined : Number(v));
                      }}
                      className={'border-none'}
                      py={'py-2'}
                      endIcon={
                        isTemperature ? TemperatureDegreeIcon : undefined
                      }
                      error={errors?.rangeConfig?.max?.message}
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}

        {showBoundEditors && isTextual && (
          <>
            <div className="flex min-w-[180px] flex-1 items-center gap-2">
              <span className="text-textColor shrink-0">
                {t('forms.dialog.range.from')}
              </span>
              <div className="min-w-0 flex-1">
                <AppMUIAutocomplete<TextualFromOption, false, true, false>
                  size="small"
                  fullWidth
                  options={textualFromOptions}
                  value={selectedTextualFrom}
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  onChange={(_e, val) => {
                    if (!val) return;
                    setValue(
                      `questions.${index}.rangeConfig.minLabel` as any,
                      val.value,
                      {shouldDirty: true},
                    );
                    setValue(
                      `questions.${index}.rangeConfig.min` as any,
                      undefined,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  disableClearable
                />
              </div>
            </div>

            <div className="flex min-w-[180px] flex-1 items-center gap-2">
              <span className="text-textColor shrink-0">
                {t('forms.dialog.range.to')}
              </span>
              <div className="min-w-0 flex-1">
                <AppMUIAutocomplete<TextualToOption, false, true, false>
                  size="small"
                  fullWidth
                  options={textualToOptions}
                  value={selectedTextualTo}
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  onChange={(_e, val) => {
                    if (!val) return;
                    setValue(
                      `questions.${index}.rangeConfig.maxLabel` as any,
                      val.value,
                      {shouldDirty: true},
                    );
                    setValue(
                      `questions.${index}.rangeConfig.max` as any,
                      undefined,
                      {
                        shouldDirty: true,
                      },
                    );
                  }}
                  disableClearable
                />
              </div>
            </div>
          </>
        )}
      </div>

      {showNumericRangeFormSlider && (
        <div className="px-3 pt-2">
          <AppRangeSlider
            value={rangeSliderDraft}
            onChange={(_e, val) => {
              const [a, b] = val as number[];
              setRangeSliderDraft([Math.min(a, b), Math.max(a, b)]);
            }}
            valueLabelDisplay="on"
            disableSwap
            min={numericSafeMin}
            max={numericSafeMax}
            valueLabelFormat={isTemperature ? v => `${v} °C` : undefined}
            size="medium"
            sx={{width: 345}}
            containerClassName="justify-end"
            startLabel={
              isTemperature ? `${rangeSliderDraft[0]} °C` : rangeSliderDraft[0]
            }
            endLabel={
              isTemperature ? `${rangeSliderDraft[1]} °C` : rangeSliderDraft[1]
            }
          />
        </div>
      )}

      {/* Single-thumb preview (not used for numeric/temperature + “range” form — dual slider is above) */}
      {(isTextual || (isNumericLike && !isFormRange)) && (
        <div className="px-3 pt-2">
          {isTextual ? (
            <AppSingleSlider
              value={textualSliderValue}
              onChange={(_e, val) => setPreviewValue(val as number)}
              min={0}
              max={100}
              valueLabelDisplay="off"
              sx={{width: 164}}
              containerClassName="justify-end"
              startLabel={
                isTextual
                  ? fromEndLabelText
                  : isTemperature
                    ? `${numericSafeMin} °C`
                    : numericSafeMin
              }
              endLabel={
                isTextual
                  ? toEndLabelText
                  : isTemperature
                    ? `${numericSafeMax} °C`
                    : numericSafeMax
              }
            />
          ) : (
            <AppSingleSlider
              value={numericSliderValue}
              onChange={(_e, val) => setPreviewValue(val as number)}
              min={numericSafeMin}
              max={numericSafeMax}
              valueLabelDisplay="on"
              valueLabelFormat={isTemperature ? v => `${v} °C` : undefined}
              size="medium"
              sx={{width: 164}}
              containerClassName="justify-end"
              startLabel={
                isTextual
                  ? fromEndLabelText
                  : isTemperature
                    ? `${numericSafeMin} °C`
                    : numericSafeMin
              }
              endLabel={
                isTextual
                  ? toEndLabelText
                  : isTemperature
                    ? `${numericSafeMax} °C`
                    : numericSafeMax
              }
            />
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Controller
          name={`questions.${index}.rangeConfig.canUploadImage` as any}
          control={control}
          render={({field}) => (
            <AppSwitch checked={!!field.value} onChange={field.onChange} />
          )}
        />
        <span className="text-[14px] text-textColor">
          {t('forms.dialog.range.allowImageUpload')}
        </span>
      </div>
    </div>
  );
}

export default RangeField;
